import { useEffect, useState } from 'react';
import PermissionsContext from '../contexts/PermissionsContext';

/**
 * AuthInitializer - Verifies admin session with the backend on every page load.
 * Calls GET /api/admin/auth/me (uses the HTTP-only accessToken cookie automatically).
 * On success: stores user in localStorage and sets permissions.
 * On 401 or error: clears localStorage and sets empty permissions.
 */
const AuthInitializer = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await fetch('/api/admin/auth/me', {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          localStorage.removeItem('adminUser');
          setPermissions([]);
          setInitialized(true);
          return;
        }

        const user = await response.json();
        localStorage.setItem('adminUser', JSON.stringify(user));

        if (user.platformRole === 'PLATFORM_OWNER') {
          setPermissions(['*']);
        } else {
          setPermissions(user.permissions || []);
        }
      } catch {
        localStorage.removeItem('adminUser');
        setPermissions([]);
      } finally {
        setInitialized(true);
      }
    };

    initAuth();
  }, []);

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300 font-medium">Initializing Admin Portal...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionsContext.Provider value={permissions}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default AuthInitializer;
